import { cn } from '@/lib/utils';
import {
    EXPERIENCE_LABELS,
    type MainScreenRow,
} from '@/hooks/useEarlyLandingAnalytics';
import { LayoutGrid, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { formatLiftPp, liftSampleReady } from '@/components/analytics/sampleSize';

const SIGNAL_LABELS: Record<string, { label: string; className: string }> = {
    retenedor_fuerte: { label: 'Retenedor fuerte', className: 'bg-emerald-500/15 text-emerald-400' },
    bajo_impacto: { label: 'Bajo impacto', className: 'bg-rose-500/15 text-rose-400' },
    habit_sin_retencion: { label: 'Hábito sin retención', className: 'bg-amber-500/15 text-amber-400' },
    muestra_pequeña: { label: 'Muestra pequeña', className: 'bg-muted text-muted-foreground' },
    neutro: { label: 'Neutro', className: 'bg-muted/60 text-muted-foreground' },
};

const SCREEN_ORDER = ['comunidad', 'rincon', 'tienda', 'perfil'];

interface MainScreensPanelProps {
    rows: MainScreenRow[];
}

export function MainScreensPanel({ rows }: MainScreensPanelProps) {
    if (!rows.length) {
        return (
            <div className="rounded-xl border border-border bg-card p-4 text-xs text-muted-foreground">
                Sin datos de pantallas × perfil en esta cohorte.
            </div>
        );
    }

    const screens = SCREEN_ORDER.filter((k) => rows.some((r) => r.screen_key === k));

    return (
        <div className="rounded-xl border border-border bg-card p-4 space-y-4">
            <div className="flex items-start gap-2">
                <LayoutGrid size={16} className="text-primary mt-0.5 shrink-0" />
                <div>
                    <h3 className="text-sm font-semibold text-foreground">
                        Valor por pantalla principal × perfil
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        Lift = % que llega a sesión 5 (o D7) entre quienes visitaron la pantalla en 48 h
                        vs quienes no. Si un lado tiene menos de 30 personas, el lift no se calcula.
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                {screens.map((screenKey) => {
                    const screenRows = rows
                        .filter((r) => r.screen_key === screenKey)
                        .sort((a, b) => b.users_in_profile - a.users_in_profile);
                    const label = screenRows[0]?.screen_label ?? screenKey;
                    return (
                        <div key={screenKey} className="space-y-2">
                            <p className="text-xs font-semibold text-foreground">{label}</p>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs min-w-[640px]">
                                    <thead>
                                        <tr className="text-muted-foreground border-b border-border">
                                            <th className="text-left py-2 font-medium">Perfil</th>
                                            <th className="text-right py-2 font-medium">n</th>
                                            <th className="text-right py-2 font-medium">% visita</th>
                                            <th className="text-right py-2 font-medium">S5 visitó</th>
                                            <th className="text-right py-2 font-medium">S5 no</th>
                                            <th className="text-right py-2 font-medium">Lift S5</th>
                                            <th className="text-right py-2 font-medium">Lift D7</th>
                                            <th className="text-left py-2 pl-3 font-medium">Señal</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {screenRows.map((r) => {
                                            const skipN = r.users_in_profile - r.visitors;
                                            const ready = liftSampleReady(r.visitors, skipN);
                                            const signalKey = ready ? r.value_signal : 'muestra_pequeña';
                                            const sig = SIGNAL_LABELS[signalKey] ?? SIGNAL_LABELS.neutro;
                                            const liftS5 = formatLiftPp(r.lift_session_5, ready);
                                            const liftD7 = formatLiftPp(r.lift_day_7, ready);
                                            return (
                                                <tr key={`${r.screen_key}-${r.profile}`} className="border-b border-border/40">
                                                    <td className="py-2 text-foreground font-medium">
                                                        {EXPERIENCE_LABELS[r.profile] || r.profile}
                                                    </td>
                                                    <td className="py-2 text-right text-muted-foreground">
                                                        {r.users_in_profile}
                                                        <span className="block text-[10px]">visitó {r.visitors} · no {skipN}</span>
                                                    </td>
                                                    <td className="py-2 text-right">{r.visit_pct}%</td>
                                                    <td className="py-2 text-right text-emerald-400/90">{ready && r.visitor_session_5_pct != null ? `${r.visitor_session_5_pct}%` : '—'}</td>
                                                    <td className="py-2 text-right text-rose-400/80">{ready && r.skip_session_5_pct != null ? `${r.skip_session_5_pct}%` : '—'}</td>
                                                    <td className={cn(
                                                        'py-2 text-right font-semibold',
                                                        !ready ? 'text-muted-foreground' : r.lift_session_5 >= 0 ? 'text-emerald-400' : 'text-rose-400'
                                                    )}>
                                                        <span className="inline-flex items-center gap-1 justify-end">
                                                            {ready && r.lift_session_5 > 2
                                                                ? <TrendingUp size={11} />
                                                                : ready && r.lift_session_5 < -2
                                                                    ? <TrendingDown size={11} />
                                                                    : ready ? <Minus size={11} /> : null}
                                                            {liftS5}
                                                        </span>
                                                    </td>
                                                    <td className={cn(
                                                        'py-2 text-right font-semibold',
                                                        !ready ? 'text-muted-foreground' : r.lift_day_7 >= 0 ? 'text-emerald-400' : 'text-rose-400'
                                                    )}>
                                                        {liftD7}
                                                    </td>
                                                    <td className="py-2 pl-3">
                                                        <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', sig.className)}>
                                                            {sig.label}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
